import { findBoardByName, getPost, listPosts, resolveMediaUrl, type PostListItem } from './api';
import { withBase } from './base';
import { applySeo } from './seo';

const ARCHIVE_BOARD_NAME = '자료실';
const FAQ_BOARD_NAME = '자주 묻는 질문';
const HOME_PAGE_SIZE = 3;
const LIST_PAGE_SIZE = 10;

type BoardKind = 'archive' | 'faq';

const BOARD_NAMES: Record<BoardKind, string> = {
  archive: ARCHIVE_BOARD_NAME,
  faq: FAQ_BOARD_NAME,
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(iso: string): { display: string; datetime: string } {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { display: iso.slice(0, 10).replace(/-/g, '.'), datetime: iso.slice(0, 10) };
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return { display: `${year}.${month}.${day}`, datetime: `${year}-${month}-${day}` };
}

function excerpt(summary: string | null, fallbackTitle: string): string {
  if (summary && summary.trim()) return summary.trim();
  return fallbackTitle;
}

function moreIcon(): string {
  return withBase('/assets/icons/more-arrow.svg');
}

function listHref(kind: BoardKind, postId: number): string {
  return withBase(kind === 'faq' ? `/faq?id=${postId}` : `/board?id=${postId}`);
}

function pageHref(kind: BoardKind, page: number): string {
  const path = kind === 'faq' ? '/faq' : '/board';
  return withBase(page > 1 ? `${path}?page=${page}` : path);
}

function renderListItem(kind: BoardKind, post: PostListItem, variant: 'home' | 'board'): string {
  const { display, datetime } = formatDate(post.created_at);
  const desc = excerpt(post.summary, post.title);
  const href = listHref(kind, post.id);

  if (variant === 'home') {
    return `
      <a class="board__item" href="${href}">
        <time class="board__date" datetime="${datetime}">${display}</time>
        <div class="board__body">
          <p class="board__item-title">${escapeHtml(post.title)}</p>
          <p class="board__item-desc">${escapeHtml(desc)}</p>
        </div>
        <span class="board__more">
          <span class="board__more-label">MORE</span>
          <img class="board__more-icon" src="${moreIcon()}" alt="" width="31" height="17" />
        </span>
      </a>
    `;
  }

  return `
    <a class="board-list__item" href="${href}">
      <time class="board-list__date" datetime="${datetime}">${display}</time>
      <div class="board-list__body">
        <p class="board-list__title">${escapeHtml(post.title)}</p>
        <p class="board-list__desc">${escapeHtml(desc)}</p>
      </div>
      <span class="board-list__more">
        <span>MORE</span>
        <img src="${moreIcon()}" alt="" width="31" height="17" />
      </span>
    </a>
  `;
}

function renderPagination(
  kind: BoardKind,
  page: number,
  totalPages: number,
  variant: 'home' | 'board',
  homeOnly = false,
): string {
  const pages = Math.max(1, totalPages);
  const itemClass = variant === 'home' ? 'board__page' : 'board-list__page';
  const items = Array.from({ length: pages }, (_, index) => {
    const current = index + 1;
    const currentAttr = current === page ? ' aria-current="page"' : '';
    if (homeOnly) {
      return `<button type="button" class="${itemClass}" data-home-page="${current}"${currentAttr}>${current}</button>`;
    }
    return `<a class="${itemClass}" href="${pageHref(kind, current)}"${currentAttr}>${current}</a>`;
  });
  return items.join('');
}

function currentPageFromUrl(): number {
  const page = Number(new URLSearchParams(window.location.search).get('page') || '1');
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function setMessage(target: HTMLElement, message: string, extraClass: string) {
  target.innerHTML = `<p class="${extraClass}">${escapeHtml(message)}</p>`;
}

export function initHomePosts(root: HTMLElement): () => void {
  const list = root.querySelector<HTMLElement>('[data-home-posts]');
  const pagination = root.querySelector<HTMLElement>('[data-home-pagination]');
  if (!list) return () => {};

  let page = 1;
  let disposed = false;

  const load = async (nextPage: number) => {
    page = nextPage;
    list.innerHTML = `<p class="board__empty">게시물을 불러오는 중입니다.</p>`;
    if (pagination) pagination.innerHTML = '';

    try {
      const board = await findBoardByName(ARCHIVE_BOARD_NAME);
      if (!board) {
        if (!disposed) setMessage(list, '자료실 게시판을 찾을 수 없습니다.', 'board__empty');
        return;
      }

      const data = await listPosts(board.slug, page, HOME_PAGE_SIZE);
      if (disposed) return;

      if (data.items.length === 0) {
        setMessage(list, '등록된 게시물이 없습니다.', 'board__empty');
        if (pagination) pagination.innerHTML = renderPagination('archive', 1, 1, 'home', true);
        return;
      }

      list.innerHTML = data.items.map((post) => renderListItem('archive', post, 'home')).join('');
      if (pagination) {
        pagination.innerHTML = renderPagination('archive', data.page, data.total_pages, 'home', true);
      }
    } catch {
      if (!disposed) setMessage(list, '게시물을 불러오지 못했습니다.', 'board__empty');
    }
  };

  const onClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>('[data-home-page]');
    if (!button || !pagination?.contains(button)) return;
    const next = Number(button.dataset.homePage || '1');
    if (next !== page) void load(next);
  };

  pagination?.addEventListener('click', onClick);
  void load(1);

  return () => {
    disposed = true;
    pagination?.removeEventListener('click', onClick);
  };
}

function preparePostContent(html: string): string {
  const value = html?.trim() ?? '';
  if (!value) return '<p>등록된 내용이 없습니다.</p>';
  if (!value.includes('<') && /&lt;\/?[a-z]/i.test(value)) {
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');
  }
  return value;
}

function rewriteContentAssets(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>('img[src], source[src], video[src], iframe[src]').forEach((el) => {
    const src = el.getAttribute('src');
    if (src) el.setAttribute('src', resolveMediaUrl(src));
  });
  container.querySelectorAll('a[href]').forEach((el) => {
    const href = el.getAttribute('href');
    if (!href || /^(mailto:|tel:|#|javascript:)/i.test(href)) return;
    el.setAttribute('href', resolveMediaUrl(href));
  });
}

function renderDetail(kind: BoardKind, post: Awaited<ReturnType<typeof getPost>>): string {
  const { display, datetime } = formatDate(post.created_at);
  const backHref = withBase(kind === 'faq' ? '/faq' : '/board');
  const files = (post.files ?? [])
    .filter((file) => file.url)
    .map(
      (file) => `
        <li>
          <a href="${escapeHtml(resolveMediaUrl(file.url))}" target="_blank" rel="noopener noreferrer">
            ${escapeHtml(file.original_name)}
          </a>
        </li>
      `,
    )
    .join('');

  return `
    <article class="board-detail" tabindex="-1">
      <time class="board-detail__date" datetime="${datetime}">${display}</time>
      <h1 class="board-detail__title">${escapeHtml(post.title)}</h1>
      <div class="board-detail__content">${preparePostContent(post.content)}</div>
      ${files ? `<ul class="board-detail__files">${files}</ul>` : ''}
      <a class="board-detail__back" href="${backHref}">목록으로</a>
    </article>
  `;
}

function scrollToBoardContent(root: ParentNode, focusArticle = false) {
  const target = root.querySelector<HTMLElement>('#board-content') ?? root.querySelector<HTMLElement>('.board-list-section');
  if (!target) return;

  const header = document.querySelector<HTMLElement>('[data-header]');
  const headerH = header?.getBoundingClientRect().height ?? 100;
  const top = target.getBoundingClientRect().top + window.scrollY - headerH - 12;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

  if (focusArticle) {
    const article = root.querySelector<HTMLElement>('.board-detail');
    article?.focus({ preventScroll: true });
  }
}

export function initBoardPage(root: HTMLElement, kind: BoardKind, postId?: string): () => void {
  const listWrap = root.querySelector<HTMLElement>('[data-board-list]');
  const pagination = root.querySelector<HTMLElement>('[data-board-pagination]');
  const section = root.querySelector<HTMLElement>('.board-list-section');
  if (!listWrap || !section) return () => {};

  let disposed = false;
  const numericId = postId ? Number(postId) : NaN;

  const loadList = async () => {
    const page = currentPageFromUrl();
    listWrap.innerHTML = `<p class="board-list__empty">게시물을 불러오는 중입니다.</p>`;
    if (pagination) pagination.innerHTML = '';

    try {
      const board = await findBoardByName(BOARD_NAMES[kind]);
      if (!board) {
        if (!disposed) setMessage(listWrap, `${BOARD_NAMES[kind]} 게시판을 찾을 수 없습니다.`, 'board-list__empty');
        return;
      }

      const data = await listPosts(board.slug, page, LIST_PAGE_SIZE);
      if (disposed) return;

      if (data.items.length === 0) {
        setMessage(listWrap, '등록된 게시물이 없습니다.', 'board-list__empty');
        if (pagination) pagination.innerHTML = renderPagination(kind, 1, 1, 'board');
        return;
      }

      listWrap.innerHTML = data.items.map((post) => renderListItem(kind, post, 'board')).join('');
      if (pagination) pagination.innerHTML = renderPagination(kind, data.page, data.total_pages, 'board');
    } catch {
      if (!disposed) setMessage(listWrap, '게시물을 불러오지 못했습니다.', 'board-list__empty');
    }
  };

  const loadDetail = async (id: number) => {
    listWrap.innerHTML = `<p class="board-list__empty">게시물을 불러오는 중입니다.</p>`;
    if (pagination) pagination.hidden = true;

    try {
      const post = await getPost(id);
      if (disposed) return;
      listWrap.innerHTML = renderDetail(kind, post);
      const content = listWrap.querySelector<HTMLElement>('.board-detail__content');
      if (content) rewriteContentAssets(content);
      applySeo({
        title: `${post.title} | 오병이어푸드`,
        description: excerpt(post.summary, post.title),
        type: 'article',
      });
      requestAnimationFrame(() => scrollToBoardContent(root, true));
    } catch {
      if (!disposed) {
        setMessage(listWrap, '게시물을 찾을 수 없습니다.', 'board-list__empty');
      }
    }
  };

  if (Number.isFinite(numericId) && numericId > 0) {
    scrollToBoardContent(root);
    void loadDetail(numericId);
  } else {
    if (pagination) pagination.hidden = false;
    void loadList();
  }

  return () => {
    disposed = true;
  };
}
